import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, BugPlay, Search } from "lucide-react";
import { toast } from "sonner";
import { Background, Controls, MiniMap, ReactFlow } from "@xyflow/react";
import "@xyflow/react/dist/style.css";

import { useLocation, useNavigate } from "react-router-dom";

import { LogCaptureControlPanel } from "@/components/admin/LogCaptureControlPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { apiClient, FRONTEND_BACKEND_URL } from "@/lib/api";

const severityClass = {
  info: "bg-cyan-900/40 text-cyan-200",
  warning: "bg-amber-900/40 text-amber-200",
  critical: "bg-rose-900/40 text-rose-200",
};

const LIFECYCLE_ORDER = ["request", "intent", "decision", "risk", "order", "execution", "fill"];

const toIsoFromDatetimeLocal = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return undefined;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return parsed.toISOString();
};

const formatUtcDisplay = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "-";
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toISOString();
};

const severityLevelBadgeClass = {
  FATAL: "bg-rose-900/50 text-rose-200 border border-rose-700",
  ERROR: "bg-orange-900/50 text-orange-200 border border-orange-700",
  WARN: "bg-amber-900/50 text-amber-200 border border-amber-700",
  INFO: "bg-slate-800 text-slate-200 border border-slate-600",
};

const rcaTagBadgeClass = {
  SCHEMA_MISMATCH: "bg-fuchsia-900/40 text-fuchsia-200 border border-fuchsia-700",
  CREDENTIAL: "bg-rose-900/40 text-rose-200 border border-rose-700",
  NETWORK: "bg-cyan-900/40 text-cyan-200 border border-cyan-700",
  UNKNOWN: "bg-slate-800 text-slate-200 border border-slate-600",
  "-": "bg-slate-900 text-slate-400 border border-slate-700",
};

const formatClientContextDisplay = (value) => {
  if (!value) return "-";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    const ip = value.ip_masked || "-";
    const ua = value.user_agent || "-";
    return `🌐 {"ip":"${ip}","ua":"${ua}"}`;
  }
  return String(value);
};

const formatBreakdownLabel = (value) => {
  const source = String(value || "-").trim();
  if (!source) return "-";
  return source.replace(/_/g, " ");
};

export const AuditLogsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    q: "",
    payload_query: "",
    severity: "",
    strategy_id: "",
    symbol: "",
    user_id: "",
    event_type: "",
    environment: "prod",
    start_time: "",
    end_time: "",
  });
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [visibleCount, setVisibleCount] = useState(40);
  const [queryLatencyMs, setQueryLatencyMs] = useState(null);

  const [savedQueries, setSavedQueries] = useState([]);
  const [savedQueryName, setSavedQueryName] = useState("");

  const [selectedCorrelation, setSelectedCorrelation] = useState("");
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [failureExplanation, setFailureExplanation] = useState(null);
  const [rootCauseBreakdown, setRootCauseBreakdown] = useState(null);
  const [replayResult, setReplayResult] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [viewMode, setViewMode] = useState("graph");
  const [showTestEvents, setShowTestEvents] = useState(false);
  const [archiveMode, setArchiveMode] = useState(false);
  const [crossEnvComparison, setCrossEnvComparison] = useState(null);
  const [integrityResult, setIntegrityResult] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState("");
  const [expandedEventRows, setExpandedEventRows] = useState({});
  const [errorWindowDays, setErrorWindowDays] = useState(30);
  const [missingCorrelationEvents, setMissingCorrelationEvents] = useState([]);
  const [missingCorrelationPage, setMissingCorrelationPage] = useState(1);
  const [expandedMissingCorrelationRows, setExpandedMissingCorrelationRows] = useState({});
  const [linkedErrorReportLoading, setLinkedErrorReportLoading] = useState(false);
  const [linkedErrorRows, setLinkedErrorRows] = useState([]);
  const [linkedErrorMeta, setLinkedErrorMeta] = useState({
    refreshedAt: null,
    returnedCount: 0,
    totalScanned: 0,
    overview: null,
  });
  const [linkedErrorWindowDays, setLinkedErrorWindowDays] = useState(30);
  const [linkedErrorStartTime, setLinkedErrorStartTime] = useState("");
  const [linkedErrorEndTime, setLinkedErrorEndTime] = useState("");
  const [linkedErrorSearch, setLinkedErrorSearch] = useState("");
  const [errorLogLoading, setErrorLogLoading] = useState(false);
  const [errorLogItems, setErrorLogItems] = useState([]);
  const [errorLogPage, setErrorLogPage] = useState(1);
  const [errorLogMeta, setErrorLogMeta] = useState({
    total: 0,
    pageCount: 1,
    hasPrev: false,
    hasNext: false,
    refreshedAt: null,
  });
  const [logCaptureLoading, setLogCaptureLoading] = useState(false);
  const [logCaptureSaving, setLogCaptureSaving] = useState(false);
  const [logCaptureDirty, setLogCaptureDirty] = useState(false);
  const [logCaptureForm, setLogCaptureForm] = useState({
    ultra_log_enabled: false,
    duration_key: "1h",
    normal_logs_limit_mb: 1024,
    ultra_logs_limit_mb: 512,
  });
  const [logCaptureMeta, setLogCaptureMeta] = useState({
    settings: null,
    options: { duration_options: [], limit_preset_options: [] },
    normal_storage: null,
    ultra_storage: null,
  });

  const hydrateLogCaptureState = useCallback((data, { respectDirty = true } = {}) => {
    setLogCaptureMeta({
      settings: data?.settings || null,
      options: data?.options || { duration_options: [], limit_preset_options: [] },
      normal_storage: data?.normal_storage || null,
      ultra_storage: data?.ultra_storage || null,
    });
    if (!respectDirty || !logCaptureDirty) {
      setLogCaptureForm({
        ultra_log_enabled: Boolean(data?.settings?.ultra_log_enabled),
        duration_key: data?.settings?.duration_key || "1h",
        normal_logs_limit_mb: Number(data?.settings?.normal_logs_limit_mb || 1024),
        ultra_logs_limit_mb: Number(data?.settings?.ultra_logs_limit_mb || 512),
      });
    }
  }, [logCaptureDirty]);

  const fetchLogCaptureSettings = useCallback(async ({ silent = false, respectDirty = true } = {}) => {
    if (!silent) {
      setLogCaptureLoading(true);
    }
    try {
      const { data } = await apiClient.get("/audit-logs/admin/log-capture/settings");
      hydrateLogCaptureState(data, { respectDirty });
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Log capture ayarları alınamadı", { id: "audit-log-capture-fetch-fail" });
    } finally {
      if (!silent) {
        setLogCaptureLoading(false);
      }
    }
  }, [hydrateLogCaptureState]);

  const saveLogCaptureSettings = useCallback(async () => {
    if (logCaptureForm.ultra_log_enabled && !logCaptureForm.duration_key) {
      toast.error("Ultra log için süre seçin", { id: "audit-log-capture-duration-required" });
      return;
    }
    setLogCaptureSaving(true);
    try {
      const payload = {
        ultra_log_enabled: Boolean(logCaptureForm.ultra_log_enabled),
        duration_key: logCaptureForm.duration_key || "1h",
        normal_logs_limit_mb: Number(logCaptureForm.normal_logs_limit_mb || 1024),
        ultra_logs_limit_mb: Number(logCaptureForm.ultra_logs_limit_mb || 512),
      };
      const { data } = await apiClient.put("/audit-logs/admin/log-capture/settings", payload);
      hydrateLogCaptureState(data, { respectDirty: false });
      setLogCaptureDirty(false);
      toast.success("Log capture ayarları kaydedildi", { id: "audit-log-capture-save-success" });
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Log capture ayarları kaydedilemedi", { id: "audit-log-capture-save-fail" });
    } finally {
      setLogCaptureSaving(false);
    }
  }, [hydrateLogCaptureState, logCaptureForm]);

  const setLogCaptureField = useCallback((field, value) => {
    setLogCaptureDirty(true);
    setLogCaptureForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const fetchSavedQueries = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/audit-logs/saved-queries", { params: { limit: 50 } });
      setSavedQueries(data?.items || []);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Saved query listesi alınamadı");
    }
  }, []);

  const fetchErrorLogs = useCallback(async ({ page = 1, windowDays = errorWindowDays, silent = false } = {}) => {
    if (!silent) {
      setErrorLogLoading(true);
    }
    try {
      const { data } = await apiClient.get("/audit-logs/admin/log-feed", {
        params: {
          include_error_only: true,
          actor_roles: "admin,user,system",
          page,
          page_size: 50,
          window_days: windowDays,
        },
      });
      setErrorLogItems(Array.isArray(data?.items) ? data.items : []);
      const pagination = data?.error_pagination || {};
      setErrorLogMeta({
        total: Number(pagination.total || 0),
        pageCount: Number(pagination.page_count || 1),
        hasPrev: Boolean(pagination.has_prev),
        hasNext: Boolean(pagination.has_next),
        refreshedAt: data?.refreshed_at || null,
      });
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Hata logları alınamadı", { id: "audit-error-log-feed-fail" });
    } finally {
      if (!silent) {
        setErrorLogLoading(false);
      }
    }
  }, [errorWindowDays]);

  const fetchLinkedErrorReport = useCallback(async ({
    windowDays = 1,
    startTime = "",
    endTime = "",
    search = "",
    silent = false,
  } = {}) => {
    if (!silent) {
      setLinkedErrorReportLoading(true);
    }

    try {
      const startIso = toIsoFromDatetimeLocal(startTime);
      const endIso = toIsoFromDatetimeLocal(endTime);
      const hasCustomRange = Boolean(startIso || endIso);

      const params = {
        limit: 100,
        actor_roles: "admin,user,system",
        q: search || undefined,
        window_days: hasCustomRange ? undefined : windowDays,
        start_time: hasCustomRange ? startIso : undefined,
        end_time: hasCustomRange ? endIso : undefined,
      };

      const { data } = await apiClient.get("/audit-logs/admin/error-table-report", { params });
      setLinkedErrorRows(Array.isArray(data?.items) ? data.items : []);
      setLinkedErrorMeta({
        refreshedAt: data?.refreshed_at || null,
        returnedCount: Number(data?.returned_count || 0),
        totalScanned: Number(data?.total_scanned || 0),
        overview: data?.overview || null,
      });
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Linked incidents hata raporu alınamadı", {
        id: "audit-linked-error-report-fail",
      });
    } finally {
      if (!silent) {
        setLinkedErrorReportLoading(false);
      }
    }
  }, []);

  const downloadLinkedErrorReport = useCallback(async (format = "json") => {
    try {
      const startIso = toIsoFromDatetimeLocal(linkedErrorStartTime);
      const endIso = toIsoFromDatetimeLocal(linkedErrorEndTime);
      const hasCustomRange = Boolean(startIso || endIso);
      const normalizedFormat = format === "xlsx" ? "xlsx" : "json";

      const { data, headers } = await apiClient.get("/audit-logs/admin/error-table-report", {
        params: {
          limit: 100,
          actor_roles: "admin,user,system",
          q: linkedErrorSearch || undefined,
          window_days: hasCustomRange ? undefined : linkedErrorWindowDays,
          start_time: hasCustomRange ? startIso : undefined,
          end_time: hasCustomRange ? endIso : undefined,
          export_format: normalizedFormat,
          download: true,
        },
        responseType: "blob",
      });

      const fallbackName = `linked_incidents_error_report_${Date.now()}.${normalizedFormat}`;
      const disposition = String(headers?.["content-disposition"] || "");
      const extracted = disposition.match(/filename="?([^";]+)"?/i)?.[1];
      const filename = extracted || fallbackName;

      const blobUrl = window.URL.createObjectURL(data);
      const anchor = document.createElement("a");
      anchor.href = blobUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(blobUrl);

      toast.success(`${normalizedFormat.toUpperCase()} raporu indirildi`, { id: `audit-linked-error-export-${normalizedFormat}` });
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Rapor indirilemedi", {
        id: `audit-linked-error-export-${format}-fail`,
      });
    }
  }, [linkedErrorEndTime, linkedErrorSearch, linkedErrorStartTime, linkedErrorWindowDays]);

  const fetchSummaries = useCallback(async (cursor = null, overrides = null) => {
    const activeFilters = overrides || filters;
    setLoading(true);
    try {
      const { data } = await apiClient.get("/audit-logs/trading-lifecycle", {
        params: {
          limit: 150,
          cursor: cursor || undefined,
          q: activeFilters.q || undefined,
          payload_query: activeFilters.payload_query || undefined,
          severity: activeFilters.severity || undefined,
          strategy_id: activeFilters.strategy_id || undefined,
          symbol: activeFilters.symbol || undefined,
          user_id: activeFilters.user_id || undefined,
          event_type: activeFilters.event_type || undefined,
          environment: activeFilters.environment || undefined,
          start_time: activeFilters.start_time || undefined,
          end_time: activeFilters.end_time || undefined,
          include_test_events: showTestEvents,
          archive_mode: archiveMode,
        },
      });
      const incomingItems = data?.items || [];
      if (cursor) {
        setItems((prev) => {
          const map = new Map(prev.map((item) => [item.correlation_id, item]));
          incomingItems.forEach((item) => map.set(item.correlation_id, item));
          return Array.from(map.values());
        });
      } else {
        setItems(incomingItems);
      }
      setNextCursor(data?.next_cursor || null);
      setHasMore(Boolean(data?.has_more));
      setVisibleCount(40);
      setQueryLatencyMs(data?.query_latency_ms || null);
      const missingIds = Array.isArray(data?.missing_correlation_event_ids) ? data.missing_correlation_event_ids : [];
      const missingEvents = Array.isArray(data?.missing_correlation_events) ? data.missing_correlation_events : [];
      const fallbackEvents = missingIds.map((eventId) => ({
        event_id: String(eventId || ""),
        event_type: "unknown_event",
        lifecycle_stage: "request",
        severity: "WARNING",
        parent_event_id: null,
        relation_status: "root",
        error_address: "/api/audit-logs/trading-lifecycle",
        error_message: "Correlation zincirinde eksik event tespit edildi",
        payload: {},
      }));

      setMissingCorrelationEvents(missingEvents.length > 0 ? missingEvents : fallbackEvents);
      setMissingCorrelationPage(1);
      setExpandedMissingCorrelationRows({});
      if (missingIds.length) {
        toast.warning(`Correlation eksik event sayısı: ${missingIds.length}`);
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Trading lifecycle listesi alınamadı");
    } finally {
      setLoading(false);
    }
  }, [archiveMode, filters, showTestEvents]);

  useEffect(() => {
    fetchSummaries(null);
    fetchSavedQueries();
    fetchLogCaptureSettings();
  }, [fetchLogCaptureSettings, fetchSavedQueries, fetchSummaries]);

  useEffect(() => {
    fetchLinkedErrorReport({ windowDays: 30, startTime: "", endTime: "", search: "" });
  }, [fetchLinkedErrorReport]);

  useEffect(() => {
    fetchErrorLogs({ page: errorLogPage, windowDays: errorWindowDays });
  }, [errorLogPage, errorWindowDays, fetchErrorLogs]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) {
        return;
      }
      fetchErrorLogs({ page: errorLogPage, windowDays: errorWindowDays, silent: true });
    }, 5000);
    return () => clearInterval(timer);
  }, [errorLogPage, errorWindowDays, fetchErrorLogs]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) {
        return;
      }
      fetchLogCaptureSettings({ silent: true, respectDirty: true });
    }, 15000);
    return () => clearInterval(timer);
  }, [fetchLogCaptureSettings]);

  const fetchIncidents = useCallback(async (correlationId) => {
    if (!correlationId) {
      setIncidents([]);
      return;
    }
    try {
      const { data } = await apiClient.get("/audit-logs/incidents", {
        params: { linked_correlation_id: correlationId, limit: 20 },
      });
      setIncidents(data?.items || []);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Incident listesi alınamadı");
    }
  }, []);

  const fetchExplainForCorrelation = useCallback(async (correlationId) => {
    if (!correlationId) return;
    setDetailLoading(true);
    try {
      const { data } = await apiClient.post("/audit-logs/explain", { correlation_id: correlationId });
      setFailureExplanation(data || null);
      setRootCauseBreakdown(data?.root_cause_breakdown || null);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Explain failure alınamadı");
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const openLifecycle = useCallback(async (correlationId) => {
    setSelectedCorrelation(correlationId);
    setFailureExplanation(null);
    setRootCauseBreakdown(null);
    setReplayResult(null);
    setCrossEnvComparison(null);
    setIntegrityResult(null);
    setSelectedEventId("");
    setExpandedEventRows({});
    setDetailLoading(true);
    try {
      const { data } = await apiClient.get(`/audit-logs/lifecycle/${encodeURIComponent(correlationId)}`);
      setDetail(data);
      setRootCauseBreakdown(data?.root_cause_breakdown || null);
      fetchIncidents(correlationId);
      await fetchExplainForCorrelation(correlationId);
    } catch (error) {
      setDetail(null);
      toast.error(error?.response?.data?.detail || "Lifecycle detayı alınamadı");
    } finally {
      setDetailLoading(false);
    }
  }, [fetchExplainForCorrelation, fetchIncidents]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const correlationId = params.get("correlation_id");
    if (correlationId && correlationId !== selectedCorrelation) {
      openLifecycle(correlationId);
    }
  }, [location.search, openLifecycle, selectedCorrelation]);

  const explainFailure = useCallback(() => {
    if (!selectedCorrelation) return;
    return fetchExplainForCorrelation(selectedCorrelation);
  }, [fetchExplainForCorrelation, selectedCorrelation]);

  useEffect(() => {
    if (!selectedCorrelation && items.length > 0) {
      openLifecycle(items[0].correlation_id);
    }
  }, [items, openLifecycle, selectedCorrelation]);

  const saveCurrentQuery = useCallback(async () => {
    const trimmedName = savedQueryName.trim();
    if (!trimmedName) {
      toast.error("Saved query adı girin");
      return;
    }
    try {
      await apiClient.post("/audit-logs/saved-queries", { name: trimmedName, params: filters });
      setSavedQueryName("");
      fetchSavedQueries();
      toast.success("Query kaydedildi");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Query kaydedilemedi");
    }
  }, [fetchSavedQueries, filters, savedQueryName]);

  const applySavedQuery = useCallback((savedId) => {
    const selected = savedQueries.find((item) => item.id === savedId);
    if (!selected) return;
    const params = selected.params || {};
    const nextFilters = {
      ...filters,
      ...params,
      q: String(params.q || ""),
      payload_query: String(params.payload_query || ""),
      severity: String(params.severity || ""),
      strategy_id: String(params.strategy_id || ""),
      symbol: String(params.symbol || ""),
      user_id: String(params.user_id || ""),
      event_type: String(params.event_type || ""),
      environment: String(params.environment || ""),
      start_time: String(params.start_time || ""),
      end_time: String(params.end_time || ""),
    };
    setFilters(nextFilters);
    fetchSummaries(null, nextFilters);
    toast.success(`Saved query uygulandı: ${selected.name}`);
  }, [fetchSummaries, filters, savedQueries]);

  const createIncident = useCallback(async (correlationId = null, rcaData = null) => {
    const targetCorrelation = correlationId || selectedCorrelation;
    if (!targetCorrelation) return;
    try {
      const resolvedRca = rcaData || failureExplanation || {};
      const payload = {
        title: `Lifecycle Incident ${targetCorrelation.slice(0, 10)}`,
        severity: "CRITICAL",
        tags: ["manual", "debug"],
        linked_correlation_id: targetCorrelation,
        source_event_id: detail?.chain?.break_step?.event_id || null,
        root_cause: resolvedRca?.root_cause || rootCauseBreakdown?.root_cause || null,
        cluster_id: rootCauseBreakdown?.cluster_id || null,
        details: {
          pattern_tag: rootCauseBreakdown?.pattern_tag || null,
          missing_critical_stages: detail?.chain?.missing_critical_stages || [],
        },
      };
      await apiClient.post("/audit-logs/incidents", payload);
      toast.success("Incident oluşturuldu");
      fetchIncidents(targetCorrelation);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Incident oluşturulamadı");
    }
  }, [detail?.chain?.break_step?.event_id, detail?.chain?.missing_critical_stages, failureExplanation, fetchIncidents, rootCauseBreakdown?.cluster_id, rootCauseBreakdown?.pattern_tag, rootCauseBreakdown?.root_cause, selectedCorrelation]);

  const closeIncident = useCallback(async (incidentId) => {
    try {
      await apiClient.patch(`/audit-logs/incidents/${encodeURIComponent(incidentId)}/status`, { status: "closed" });
      toast.success("Incident kapatıldı");
      fetchIncidents(selectedCorrelation);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Incident kapatılamadı");
    }
  }, [fetchIncidents, selectedCorrelation]);

  const exportIncidentBundle = useCallback((incidentId) => {
    const url = `${FRONTEND_BACKEND_URL}/api/audit-logs/incidents/${encodeURIComponent(incidentId)}/bundle`;
    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  const openIncidentLifecycle = useCallback((correlationId) => {
    if (!correlationId) {
      toast.error("Correlation bulunamadı");
      return;
    }
    navigate(`/admin/audit-logs?correlation_id=${encodeURIComponent(correlationId)}`);
  }, [navigate]);

  const loadMore = useCallback(() => {
    if (!hasMore || !nextCursor || loading) return;
    fetchSummaries(nextCursor);
  }, [fetchSummaries, hasMore, loading, nextCursor]);

  const fetchCrossEnvironmentComparison = useCallback(async () => {
    if (!selectedCorrelation) return;
    try {
      const { data } = await apiClient.get(`/audit-logs/lifecycle/compare/${encodeURIComponent(selectedCorrelation)}`, {
        params: { environments: "prod,staging,test,canary", limit: 1200 },
      });
      setCrossEnvComparison(data || null);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Cross-environment compare alınamadı");
    }
  }, [selectedCorrelation]);

  const verifyIntegrity = useCallback(async () => {
    if (!selectedCorrelation) return;
    try {
      const { data } = await apiClient.get("/audit/verify-trace", {
        params: {
          correlation_id: selectedCorrelation,
          environment: filters.environment || undefined,
        },
      });
      setIntegrityResult(data || null);
      toast.success("Integrity doğrulaması tamamlandı");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Integrity doğrulaması başarısız");
    }
  }, [filters.environment, selectedCorrelation]);

  const copyToClipboard = useCallback(async (value, successMessage) => {
    try {
      await navigator.clipboard.writeText(String(value || ""));
      toast.success(successMessage);
    } catch {
      toast.error("Kopyalama başarısız");
    }
  }, []);

  const onCopyFullTrace = useCallback(() => {
    copyToClipboard(JSON.stringify(detail?.events || detail?.chain?.events || [], null, 2), "Full trace kopyalandı");
  }, [copyToClipboard, detail?.chain?.events, detail?.events]);

  const onCopyCorrelationChain = useCallback(() => {
    const chain = (detail?.events || detail?.chain?.events || []).map((event) => event.event_id).join(" -> ");
    copyToClipboard(chain, "Correlation chain kopyalandı");
  }, [copyToClipboard, detail?.chain?.events, detail?.events]);

  const onCopySelectedEventJson = useCallback(() => {
    const eventsList = detail?.events || detail?.chain?.events || [];
    const selectedEvent = eventsList.find((event) => event.event_id === selectedEventId);
    copyToClipboard(JSON.stringify(selectedEvent || {}, null, 2), "Event JSON kopyalandı");
  }, [copyToClipboard, detail?.chain?.events, detail?.events, selectedEventId]);

  const onCopyReplayInput = useCallback(() => {
    const replayInput = {
      correlation_id: selectedCorrelation,
      replay_mode: "isolated",
      events: detail?.events || detail?.chain?.events || [],
    };
    copyToClipboard(JSON.stringify(replayInput, null, 2), "Replay input kopyalandı");
  }, [copyToClipboard, detail?.chain?.events, detail?.events, selectedCorrelation]);

  const runReplay = useCallback(async () => {
    if (!selectedCorrelation) return;
    setDetailLoading(true);
    try {
      const { data } = await apiClient.post(`/audit-logs/trading-lifecycle/${encodeURIComponent(selectedCorrelation)}/replay`);
      setReplayResult(data);
      toast.success("Replay çalıştırıldı");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Replay başarısız");
    } finally {
      setDetailLoading(false);
    }
  }, [selectedCorrelation]);

  const stats = useMemo(
    () => ({
      total: items.length,
      hasError: items.filter((item) => item.has_error).length,
      incomplete: items.filter((item) => item.trace_incomplete).length,
    }),
    [items],
  );

  const hasLifecycle = Boolean(selectedCorrelation && detail);
  const hasRca = Boolean(failureExplanation || rootCauseBreakdown);
  const hasIncident = incidents.length > 0;

  const exportBundleByCorrelation = useCallback(() => {
    if (!selectedCorrelation) {
      toast.error("Önce correlation seçin");
      return;
    }
    const matchingIncident = incidents.find((item) => item.linked_correlation_id === selectedCorrelation) || incidents[0];
    if (!matchingIncident) {
      toast.error("Önce incident oluşturun");
      return;
    }
    exportIncidentBundle(matchingIncident.incident_id);
  }, [exportIncidentBundle, incidents, selectedCorrelation]);

  const events = detail?.events || detail?.chain?.events || [];
  const renderedItems = items.slice(0, visibleCount);
  const graphData = useMemo(() => {
    const sourceEvents = detail?.events || detail?.chain?.events || [];
    const brokenEventId = failureExplanation?.explain_failure?.broken_step?.event_id || null;
    const brokenIndex = sourceEvents.findIndex((event) => event.event_id === brokenEventId);
    const nodes = sourceEvents.map((event, index) => {
      const stageIndex = Math.max(LIFECYCLE_ORDER.indexOf(event.lifecycle_stage), 0);
      const isBroken = brokenEventId && event.event_id === brokenEventId;
      const isUpstream = brokenIndex > 0 && index < brokenIndex;
      const background = isBroken ? "#7f1d1d" : isUpstream ? "#92400e" : "#1f2937";
      return {
        id: event.event_id,
        position: { x: index * 190, y: stageIndex * 130 },
        data: { label: `${event.lifecycle_stage} • ${event.event_type}` },
        style: {
          width: 180,
          borderRadius: 12,
          background,
          color: "#e5e7eb",
          border: "1px solid rgba(148,163,184,0.55)",
          fontSize: 12,
          padding: 8,
        },
      };
    });

    const edges = sourceEvents
      .map((event, index) => {
        const targetId = event.event_id;
        const sourceId = event.parent_event_id || (index > 0 ? sourceEvents[index - 1]?.event_id : null);
        if (!sourceId || !targetId || sourceId === targetId) {
          return null;
        }
        return {
          id: `${sourceId}-${targetId}`,
          source: sourceId,
          target: targetId,
          animated: true,
          style: {
            stroke: brokenEventId && targetId === brokenEventId ? "#ef4444" : "#34d399",
            strokeWidth: 2,
          },
        };
      })
      .filter(Boolean);

    return { nodes, edges };
  }, [detail?.chain?.events, detail?.events, failureExplanation?.explain_failure?.broken_step?.event_id]);

  const rootCauseSummary = useMemo(() => {
    if (failureExplanation?.root_cause) return failureExplanation.root_cause;
    if (rootCauseBreakdown?.root_cause) return rootCauseBreakdown.root_cause;
    if (rootCauseBreakdown?.failure_type) return rootCauseBreakdown.failure_type;
    if (failureExplanation?.broken_step) return `broken_step:${failureExplanation.broken_step}`;
    if (detail?.chain?.break_step?.event_type) return `broken_step:${detail.chain.break_step.event_type}`;
    if (detail?.broken_chain) return "broken_chain_detected";
    if (detail?.trace_incomplete) return "trace_incomplete";
    return "root_cause_unknown";
  }, [
    detail?.broken_chain,
    detail?.chain?.break_step?.event_type,
    detail?.trace_incomplete,
    failureExplanation?.broken_step,
    failureExplanation?.root_cause,
    rootCauseBreakdown?.failure_type,
    rootCauseBreakdown?.root_cause,
  ]);

  const errorLogPageLabel = useMemo(() => {
    const total = Number(errorLogMeta.total || 0);
    if (total <= 0) {
      return "0 / 0";
    }
    const start = (errorLogPage - 1) * 50 + 1;
    const end = Math.min(start + 49, total);
    return `${start}-${end} / ${total}`;
  }, [errorLogMeta.total, errorLogPage]);

  const globalErrorOverviewCards = useMemo(() => {
    const overview = linkedErrorMeta.overview || {};
    return [
      { key: "total", label: "Toplam hata", value: Number(overview.total_errors || 0) },
      { key: "retryable", label: "Retryable", value: Number(overview.retryable_count || 0) },
      { key: "fatal", label: "Error/Fatal", value: Number(overview.fatal_count || 0) },
      { key: "latest", label: "Son hata", value: overview.latest_error_at || "-" },
    ];
  }, [linkedErrorMeta.overview]);

  const globalErrorBreakdowns = useMemo(() => {
    const overview = linkedErrorMeta.overview || {};
    return [
      { key: "services", title: "Alan / servis", items: Array.isArray(overview.service_source_breakdown) ? overview.service_source_breakdown : [] },
      { key: "classes", title: "Hata sınıfı", items: Array.isArray(overview.error_class_breakdown) ? overview.error_class_breakdown : [] },
      { key: "rca", title: "RCA etiketi", items: Array.isArray(overview.rca_tag_breakdown) ? overview.rca_tag_breakdown : [] },
      { key: "roles", title: "Rol kaynağı", items: Array.isArray(overview.actor_role_breakdown) ? overview.actor_role_breakdown : [] },
    ];
  }, [linkedErrorMeta.overview]);

  const actionSuggestion = useMemo(() => {
    if (!selectedCorrelation) {
      return "Correlation seçin ve Explain Failure çalıştırın.";
    }
    if (detail?.missing_critical_stages?.length) {
      return `Eksik stage'leri tamamla: ${detail.missing_critical_stages.join(", ")}`;
    }
    if (detail?.broken_chain) {
      return "Parent-child zincirini onar ve orphan eventleri bağla.";
    }
    if (failureExplanation?.broken_step) {
      return `Broken step için upstream/downstream kontrolü: ${failureExplanation.broken_step}`;
    }
    if (rootCauseBreakdown?.critical_blockers?.length) {
      return `Kritik blockerları çöz: ${rootCauseBreakdown.critical_blockers.join(", ")}`;
    }
    return "Replay + Verify Integrity ile doğrula.";
  }, [detail?.broken_chain, detail?.missing_critical_stages, failureExplanation?.broken_step, rootCauseBreakdown?.critical_blockers, selectedCorrelation]);

  const missingCorrelationRows = useMemo(() => {
    return (missingCorrelationEvents || []).map((item, index) => {
      const payload = item?.payload && typeof item.payload === "object" ? item.payload : {};
      const eventId = String(item?.event_id || item?.id || "");
      return {
        row_no: index + 1,
        event_id: eventId,
        event_type: String(item?.event_type || "unknown_event"),
        lifecycle_stage: String(item?.lifecycle_stage || "request"),
        severity: String(item?.severity || "WARNING").toUpperCase(),
        parent_event_id: String(item?.parent_event_id || "") || "-",
        relation_status: String(item?.relation_status || (item?.parent_event_id ? "child_unlinked" : "root")),
        payload,
        error_address: String(item?.error_address || item?.route || "/api/audit-logs/trading-lifecycle"),
        error_message: String(item?.error_message || item?.error_code || "Correlation ID eksik"),
        resolution_hint: String(item?.resolution_hint || "Correlation üretimini zorunlu hale getir"),
      };
    });
  }, [missingCorrelationEvents]);

  const missingCorrelationPageCount = useMemo(
    () => Math.max(Math.ceil((missingCorrelationRows.length || 0) / 50), 1),
    [missingCorrelationRows.length],
  );

  const pagedMissingCorrelationRows = useMemo(() => {
    const start = (missingCorrelationPage - 1) * 50;
    const end = start + 50;
    return missingCorrelationRows.slice(start, end);
  }, [missingCorrelationPage, missingCorrelationRows]);

  const missingCorrelationRangeLabel = useMemo(() => {
    if (missingCorrelationRows.length === 0) {
      return "0 / 0";
    }
    const start = (missingCorrelationPage - 1) * 50 + 1;
    const end = Math.min(start + 49, missingCorrelationRows.length);
    return `${start}-${end} / ${missingCorrelationRows.length}`;
  }, [missingCorrelationPage, missingCorrelationRows.length]);

  useEffect(() => {
    if (missingCorrelationPage > missingCorrelationPageCount) {
      setMissingCorrelationPage(1);
    }
  }, [missingCorrelationPage, missingCorrelationPageCount]);

  return (
    <section className="space-y-6" data-testid="audit-logs-page">
      <header className="rounded-2xl border border-cyan-800/50 bg-gradient-to-r from-slate-950 via-slate-900 to-cyan-950 p-6" data-testid="audit-logs-header">
        <h2 className="text-4xl font-black tracking-tight text-cyan-200" data-testid="audit-logs-title">Trading Lifecycle Debugger</h2>
        <p className="mt-2 text-sm text-slate-300" data-testid="audit-logs-subtitle">
          request → intent → decision → risk → order → execution → fill zincirini correlation bazında uçtan uca izle.
        </p>
        <div className="mt-4 flex flex-wrap gap-3 text-xs text-slate-300" data-testid="audit-logs-meta">
          <span data-testid="audit-logs-meta-total">chains: {stats.total}</span>
          <span data-testid="audit-logs-meta-critical">has_error: {stats.hasError}</span>
          <span data-testid="audit-logs-meta-warning">trace_incomplete: {stats.incomplete}</span>
          <span data-testid="audit-logs-meta-query-latency">query_latency_ms: {queryLatencyMs ?? "-"}</span>
        </div>
      </header>

      {missingCorrelationRows.length > 0 && (
        <section className="rounded-2xl border border-rose-700/50 bg-rose-950/20 p-4" data-testid="audit-missing-correlation-table-section">
          <div className="flex flex-wrap items-center justify-between gap-2" data-testid="audit-missing-correlation-table-header">
            <h3 className="text-base font-semibold text-rose-200" data-testid="audit-missing-correlation-table-title">
              Correlation Eksik Event Listesi
            </h3>
            <div className="flex flex-wrap items-center gap-2 text-xs text-rose-100" data-testid="audit-missing-correlation-table-meta-group">
              <span data-testid="audit-missing-correlation-table-count">toplam_hata: {missingCorrelationRows.length}</span>
              <span data-testid="audit-missing-correlation-table-page">sayfa: {missingCorrelationPage}/{missingCorrelationPageCount}</span>
              <span data-testid="audit-missing-correlation-table-range">aralık: {missingCorrelationRangeLabel}</span>
            </div>
          </div>
          <p className="mt-2 text-xs text-rose-100/90" data-testid="audit-missing-correlation-table-subtitle">
            Hata adresi + hata açıklaması, lifecycle tablo formatında satır/sütun gösterilir.
          </p>
          <div className="mt-3 overflow-x-auto" data-testid="audit-missing-correlation-table-wrap">
            <Table data-testid="audit-missing-correlation-table">
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>event_type</TableHead>
                  <TableHead>stage</TableHead>
                  <TableHead>severity</TableHead>
                  <TableHead>parent</TableHead>
                  <TableHead>relation</TableHead>
                  <TableHead>hata_adresi</TableHead>
                  <TableHead>hata</TableHead>
                  <TableHead>payload</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagedMissingCorrelationRows.map((row, index) => (
                  <Fragment key={`${row.event_id}-${index}`}>
                    <TableRow data-testid={`audit-missing-correlation-table-row-${index}`}>
                    <TableCell data-testid={`audit-missing-correlation-table-row-no-${index}`}>{row.row_no}</TableCell>
                    <TableCell data-testid={`audit-missing-correlation-table-event-type-${index}`}>
                      <p>{row.event_type || "-"}</p>
                      <p className="font-mono text-xs text-slate-400" data-testid={`audit-missing-correlation-table-event-id-${index}`}>{row.event_id || "-"}</p>
                    </TableCell>
                    <TableCell data-testid={`audit-missing-correlation-table-stage-${index}`}>{row.lifecycle_stage || "-"}</TableCell>
                    <TableCell data-testid={`audit-missing-correlation-table-severity-${index}`}>{row.severity || "-"}</TableCell>
                    <TableCell data-testid={`audit-missing-correlation-table-parent-${index}`}>{row.parent_event_id || "-"}</TableCell>
                    <TableCell data-testid={`audit-missing-correlation-table-relation-${index}`}>{row.relation_status || "root"}</TableCell>
                    <TableCell className="font-mono text-xs" data-testid={`audit-missing-correlation-table-error-address-${index}`}>{row.error_address}</TableCell>
                    <TableCell data-testid={`audit-missing-correlation-table-error-message-${index}`}>{row.error_message}</TableCell>
                    <TableCell data-testid={`audit-missing-correlation-table-payload-toggle-${index}`}>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setExpandedMissingCorrelationRows((prev) => ({
                          ...prev,
                          [row.event_id || `${index}`]: !prev[row.event_id || `${index}`],
                        }))}
                        data-testid={`audit-missing-correlation-table-expand-json-button-${index}`}
                      >
                        {expandedMissingCorrelationRows[row.event_id || `${index}`] ? "Collapse" : "Expand JSON"}
                      </Button>
                    </TableCell>
                    </TableRow>
                    {expandedMissingCorrelationRows[row.event_id || `${index}`] && (
                      <TableRow data-testid={`audit-missing-correlation-table-json-row-${index}`}>
                        <TableCell colSpan={9}>
                          <pre className="max-h-64 overflow-auto rounded bg-slate-950 p-3 text-xs text-emerald-200" data-testid={`audit-missing-correlation-table-json-${index}`}>
                            {JSON.stringify(row.payload || {}, null, 2)}
                          </pre>
                          <p className="mt-2 text-xs text-rose-100" data-testid={`audit-missing-correlation-table-resolution-${index}`}>
                            öneri: {row.resolution_hint}
                          </p>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="mt-3 flex items-center gap-2" data-testid="audit-missing-correlation-table-pagination-controls">
            <Button
              size="sm"
              variant="outline"
              disabled={missingCorrelationPage <= 1}
              onClick={() => setMissingCorrelationPage((prev) => Math.max(prev - 1, 1))}
              data-testid="audit-missing-correlation-table-pagination-prev-button"
            >
              Önceki 50
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={missingCorrelationPage >= missingCorrelationPageCount}
              onClick={() => setMissingCorrelationPage((prev) => Math.min(prev + 1, missingCorrelationPageCount))}
              data-testid="audit-missing-correlation-table-pagination-next-button"
            >
              Sonraki 50
            </Button>
          </div>
        </section>
      )}

      <LogCaptureControlPanel
        loading={logCaptureLoading}
        saving={logCaptureSaving}
        dirty={logCaptureDirty}
        form={logCaptureForm}
        meta={logCaptureMeta}
        onFieldChange={setLogCaptureField}
        onRefresh={() => fetchLogCaptureSettings({ respectDirty: true })}
        onSave={saveLogCaptureSettings}
      />

      <div className="rounded-2xl border border-slate-800 bg-slate-950/60 p-4" data-testid="audit-debug-flow-panel">
        <div className="flex flex-wrap items-start justify-between gap-2" data-testid="audit-debug-flow-header">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400" data-testid="audit-debug-flow-title">Lifecycle → RCA → Incident → Export</p>
            <p className="text-sm text-slate-300" data-testid="audit-debug-flow-subtitle">Tek ekranda debug ve aksiyon akışı.</p>
          </div>
          <p className="text-xs text-slate-400" data-testid="audit-debug-flow-selected">correlation: {selectedCorrelation || "seçilmedi"}</p>
        </div>
        <div className="mt-3 grid gap-3 md:grid-cols-4" data-testid="audit-debug-flow-steps">
          <div className="rounded-lg border border-slate-700 bg-slate-900/60 p-3" data-testid="audit-debug-flow-step-lifecycle">
            <p className="text-xs uppercase text-slate-400" data-testid="audit-debug-flow-lifecycle-label">1) Lifecycle</p>
            <p className="text-sm text-slate-100" data-testid="audit-debug-flow-lifecycle-status">status: {hasLifecycle ? "READY" : "WAITING"}</p>
            <p className="text-xs text-slate-400" data-testid="audit-debug-flow-lifecycle-hint">Correlation seçildiğinde otomatik yüklenir.</p>
          </div>
          <div className="rounded-lg border border-slate-700 bg-slate-900/60 p-3" data-testid="audit-debug-flow-step-rca">
            <p className="text-xs uppercase text-slate-400" data-testid="audit-debug-flow-rca-label">2) RCA</p>
            <p className="text-sm text-slate-100" data-testid="audit-debug-flow-rca-status">status: {hasRca ? "READY" : "WAITING"}</p>
            <Button
              size="sm"
              className="mt-2 w-full"
              onClick={explainFailure}
              disabled={!selectedCorrelation || detailLoading}
              data-testid="audit-debug-flow-rca-button"
            >
              Explain / RCA
            </Button>
          </div>
          <div className="rounded-lg border border-slate-700 bg-slate-900/60 p-3" data-testid="audit-debug-flow-step-incident">
            <p className="text-xs uppercase text-slate-400" data-testid="audit-debug-flow-incident-label">3) Incident</p>
            <p className="text-sm text-slate-100" data-testid="audit-debug-flow-incident-status">status: {hasIncident ? "READY" : "WAITING"}</p>
            <Button
              size="sm"
              className="mt-2 w-full"
              variant="outline"
              onClick={() => createIncident(selectedCorrelation, failureExplanation)}
              disabled={!hasRca || detailLoading}
              data-testid="audit-debug-flow-incident-button"
            >
              Incident Oluştur
            </Button>
          </div>
          <div className="rounded-lg border border-slate-700 bg-slate-900/60 p-3" data-testid="audit-debug-flow-step-export">
            <p className="text-xs uppercase text-slate-400" data-testid="audit-debug-flow-export-label">4) Export</p>
            <p className="text-sm text-slate-100" data-testid="audit-debug-flow-export-status">status: {hasIncident ? "READY" : "WAITING"}</p>
            <Button
              size="sm"
              className="mt-2 w-full"
              variant="secondary"
              onClick={exportBundleByCorrelation}
              disabled={!hasIncident}
              data-testid="audit-debug-flow-export-button"
            >
              Export Debug Bundle
            </Button>
          </div>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-5" data-testid="audit-logs-filter-grid">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
          <Input
            value={filters.q}
            onChange={(event) => setFilters((prev) => ({ ...prev, q: event.target.value }))}
            placeholder="correlation / reason ara"
            className="pl-9"
            data-testid="audit-logs-filter-q-input"
          />
        </div>
        <Input
          value={filters.payload_query}
          onChange={(event) => setFilters((prev) => ({ ...prev, payload_query: event.target.value }))}
          placeholder="payload full-text"
          data-testid="audit-logs-filter-payload-query-input"
        />
        <select
          value={filters.severity}
          onChange={(event) => setFilters((prev) => ({ ...prev, severity: event.target.value }))}
          className="h-10 rounded-md border border-slate-700 bg-slate-900 px-3 text-sm text-white"
          data-testid="audit-logs-filter-severity-select"
        >
          <option value="">severity: all</option>
          <option value="INFO">INFO</option>
          <option value="WARNING">WARNING</option>
          <option value="ERROR">ERROR</option>
          <option value="CRITICAL">CRITICAL</option>
        </select>
        <Input
          value={filters.strategy_id}
          onChange={(event) => setFilters((prev) => ({ ...prev, strategy_id: event.target.value }))}
          placeholder="strategy_id"
          data-testid="audit-logs-filter-strategy-id-input"
        />
        <Input
          value={filters.symbol}
          onChange={(event) => setFilters((prev) => ({ ...prev, symbol: event.target.value }))}
          placeholder="symbol"
          data-testid="audit-logs-filter-symbol-input"
        />
      </div>

      <div className="grid gap-3 md:grid-cols-5" data-testid="audit-logs-filter-grid-secondary">
        <Input
          value={filters.user_id}
          onChange={(event) => setFilters((prev) => ({ ...prev, user_id: event.target.value }))}
          placeholder="user_id"
          data-testid="audit-logs-filter-user-id-input"
        />
        <Input
          value={filters.event_type}
          onChange={(event) => setFilters((prev) => ({ ...prev, event_type: event.target.value }))}
          placeholder="event_type"
          data-testid="audit-logs-filter-event-type-input"
        />
        <select
          value={filters.environment}
          onChange={(event) => setFilters((prev) => ({ ...prev, environment: event.target.value }))}
          className="h-10 rounded-md border border-slate-700 bg-slate-900 px-3 text-sm text-white"
          data-testid="audit-logs-filter-environment-select"
        >
          <option value="prod">prod</option>
          <option value="staging">staging</option>
          <option value="test">test</option>
          <option value="canary">canary</option>
        </select>
        <Input
          value={filters.start_time}
          onChange={(event) => setFilters((prev) => ({ ...prev, start_time: event.target.value }))}
          placeholder="start_time (ISO/ms)"
          data-testid="audit-logs-filter-start-time-input"
        />
        <Input
          value={filters.end_time}
          onChange={(event) => setFilters((prev) => ({ ...prev, end_time: event.target.value }))}
          placeholder="end_time (ISO/ms)"
          data-testid="audit-logs-filter-end-time-input"
        />
      </div>

      <div className="grid gap-3 md:grid-cols-5" data-testid="audit-logs-query-actions-grid">
        <Input
          value={savedQueryName}
          onChange={(event) => setSavedQueryName(event.target.value)}
          placeholder="saved query adı"
          data-testid="audit-logs-save-query-name-input"
        />
        <Button onClick={saveCurrentQuery} variant="outline" data-testid="audit-logs-save-query-button">
          Save Query
        </Button>
        <select
          onChange={(event) => applySavedQuery(event.target.value)}
          className="h-10 rounded-md border border-slate-700 bg-slate-900 px-3 text-sm text-white"
          data-testid="audit-logs-saved-query-select"
          value=""
        >
          <option value="">saved query seç</option>
          {savedQueries.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
        <Button onClick={() => fetchSummaries(null)} disabled={loading} data-testid="audit-logs-filter-apply-button">
          Yenile
        </Button>
        <Button
          variant="secondary"
          onClick={loadMore}
          disabled={!hasMore || loading}
          data-testid="audit-logs-pagination-load-more-button"
        >
          {hasMore ? "Load more" : "No more"}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3" data-testid="audit-logs-view-and-env-controls">
        <Button
          variant={viewMode === "graph" ? "default" : "outline"}
          onClick={() => setViewMode("graph")}
          data-testid="audit-view-mode-graph-button"
        >
          Graph View (Primary)
        </Button>
        <Button
          variant={viewMode === "table" ? "default" : "outline"}
          onClick={() => setViewMode("table")}
          data-testid="audit-view-mode-table-button"
        >
          Event List (Secondary)
        </Button>
        <label className="flex items-center gap-2 text-sm text-slate-300" data-testid="audit-show-test-events-toggle-wrapper">
          <input
            type="checkbox"
            checked={showTestEvents}
            onChange={(event) => setShowTestEvents(event.target.checked)}
            data-testid="audit-show-test-events-toggle"
          />
          test eventleri göster
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-300" data-testid="audit-archive-mode-toggle-wrapper">
          <input
            type="checkbox"
            checked={archiveMode}
            onChange={(event) => setArchiveMode(event.target.checked)}
            data-testid="audit-archive-mode-toggle"
          />
          archive mode (cold)
        </label>
      </div>

      <div className="rounded-2xl border border-slate-800 bg-slate-950/50 p-3" data-testid="audit-logs-graph-panel">
        <div
          className="sticky top-0 z-10 mb-3 rounded-xl border border-slate-700/60 bg-slate-950/90 p-3 backdrop-blur"
          data-testid="audit-root-cause-sticky-panel"
        >
          <p
            className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400"
            data-testid="audit-root-cause-sticky-label"
          >
            Tek satır kök neden özeti
          </p>
          <p className="mt-1 text-sm text-slate-100" data-testid="audit-root-cause-sticky-summary">
            Kök neden:{" "}
            <span className="text-amber-200" data-testid="audit-root-cause-sticky-cause">
              {rootCauseSummary}
            </span>
            {" "}• Aksiyon:{" "}
            <span className="text-emerald-200" data-testid="audit-root-cause-sticky-action">
              {actionSuggestion}
            </span>
          </p>
        </div>
        <div className="mb-3 flex flex-wrap gap-2" data-testid="audit-logs-copy-tools">
          <Button variant="outline" size="sm" onClick={onCopyFullTrace} data-testid="audit-copy-full-trace-button">Copy full trace</Button>
          <Button variant="outline" size="sm" onClick={onCopyCorrelationChain} data-testid="audit-copy-correlation-chain-button">Copy correlation chain</Button>
          <Button variant="outline" size="sm" onClick={onCopySelectedEventJson} data-testid="audit-copy-event-json-button">Copy event JSON</Button>
          <Button variant="outline" size="sm" onClick={onCopyReplayInput} data-testid="audit-copy-replay-input-button">Copy replay input</Button>
          <Button variant="secondary" size="sm" onClick={fetchCrossEnvironmentComparison} disabled={!selectedCorrelation} data-testid="audit-cross-env-compare-button">Cross-env compare</Button>
          <Button variant="secondary" size="sm" onClick={verifyIntegrity} disabled={!selectedCorrelation} data-testid="audit-verify-integrity-button">Verify integrity</Button>
        </div>
        <p className="mb-2 text-xs text-slate-400" data-testid="audit-selected-event-id">selected_event_id: {selectedEventId || "-"}</p>
        <div className="h-[360px] overflow-hidden rounded-xl border border-slate-700" data-testid="audit-logs-reactflow-wrap">
          {viewMode === "graph" ? (
            <ReactFlow
              nodes={graphData.nodes}
              edges={graphData.edges}
              fitView
              onNodeClick={(_, node) => setSelectedEventId(node.id)}
              data-testid="audit-logs-reactflow"
            >
              <Background />
              <MiniMap pannable zoomable data-testid="audit-logs-reactflow-minimap" />
              <Controls data-testid="audit-logs-reactflow-controls" />
            </ReactFlow>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-slate-400" data-testid="audit-logs-graph-hidden-note">
              Graph görünümü pasif. "Graph View" ile aktif edin.
            </div>
          )}
        </div>
      </div>

      {viewMode === "table" && (
        <div
          className="max-h-[360px] overflow-y-auto rounded-2xl border border-slate-800"
          data-testid="audit-logs-table-wrap"
          onScroll={(event) => {
            const node = event.currentTarget;
            if (node.scrollTop + node.clientHeight >= node.scrollHeight - 24) {
              setVisibleCount((prev) => Math.min(prev + 30, items.length));
            }
          }}
        >
          <Table data-testid="audit-logs-table">
          <TableHeader>
            <TableRow>
              <TableHead>Correlation</TableHead>
              <TableHead>Window</TableHead>
              <TableHead>Chain</TableHead>
              <TableHead>Pattern</TableHead>
              <TableHead>Orphan</TableHead>
              <TableHead>Missing stages</TableHead>
              <TableHead>Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="py-8 text-center text-slate-300" data-testid="audit-logs-table-loading">yükleniyor...</TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="py-8 text-center text-slate-400" data-testid="audit-logs-table-empty">kayıt yok</TableCell>
              </TableRow>
            ) : (
              renderedItems.map((item, index) => {
                const sev = item.has_error || item.broken_chain ? "critical" : item.trace_incomplete ? "warning" : "info";
                return (
                  <TableRow key={item.correlation_id} data-testid={`audit-logs-row-${index}`}>
                    <TableCell data-testid={`audit-logs-row-id-${index}`}>{item.correlation_id}</TableCell>
                    <TableCell data-testid={`audit-logs-row-time-${index}`}>{item.started_at} → {item.ended_at}</TableCell>
                    <TableCell data-testid={`audit-logs-row-severity-${index}`}>
                      <span className={`rounded-full px-2 py-1 text-xs font-semibold ${severityClass[sev]}`}>{item.broken_chain ? "BROKEN" : "VALID"}</span>
                    </TableCell>
                    <TableCell data-testid={`audit-logs-row-pattern-${index}`}>{item.pattern_tag || "-"}</TableCell>
                    <TableCell data-testid={`audit-logs-row-action-${index}`}>{item.orphan_count}</TableCell>
                    <TableCell data-testid={`audit-logs-row-entity-${index}`}>{(item.missing_critical_stages || []).join(", ") || "none"}</TableCell>
                    <TableCell data-testid={`audit-logs-row-actor-${index}`}>
                      <Button size="sm" variant="outline" onClick={() => openLifecycle(item.correlation_id)} data-testid={`audit-open-full-lifecycle-button-${index}`}>
                        Open full lifecycle
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
          </Table>
        </div>
      )}

      <div className="rounded-2xl border border-slate-800 bg-slate-950/60 p-4" data-testid="audit-replay-panel">
        <h3 className="text-lg font-bold text-slate-200" data-testid="audit-replay-title">Open full lifecycle</h3>
        <p className="text-xs text-slate-400" data-testid="audit-replay-subtitle">seçili correlation: {selectedCorrelation || "-"}</p>
        <div className="mt-3 flex flex-wrap gap-2" data-testid="audit-replay-controls">
          <Button onClick={explainFailure} disabled={!selectedCorrelation || detailLoading} data-testid="audit-explain-failure-button">
            <AlertTriangle className="mr-1 h-4 w-4" /> Explain Failure
          </Button>
          <Button onClick={runReplay} disabled={!selectedCorrelation || detailLoading} variant="secondary" data-testid="audit-replay-run-button">
            <BugPlay className="mr-1 h-4 w-4" /> Replay
          </Button>
          <Button
            onClick={createIncident}
            disabled={!selectedCorrelation || detailLoading}
            variant="outline"
            data-testid="audit-create-incident-button"
          >
            Create Incident
          </Button>
        </div>

        {!!detail && (
          <div className="mt-4 overflow-x-auto" data-testid="audit-detail-events-wrap">
            <div className="mb-3 flex flex-wrap gap-3 text-xs text-slate-300" data-testid="audit-detail-chain-indicators">
              <span data-testid="audit-detail-broken-chain-indicator">broken_chain: {String(Boolean(detail?.broken_chain))}</span>
              <span data-testid="audit-detail-trace-incomplete-indicator">trace_incomplete: {String(Boolean(detail?.trace_incomplete))}</span>
              <span data-testid="audit-detail-missing-stages-indicator">
                missing_stages: {(detail?.missing_critical_stages || []).join(", ") || "none"}
              </span>
            </div>
            <Table data-testid="audit-detail-events-table">
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>event_type</TableHead>
                  <TableHead>stage</TableHead>
                  <TableHead>severity</TableHead>
                  <TableHead>parent</TableHead>
                  <TableHead>relation</TableHead>
                  <TableHead>payload</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.map((event, index) => (
                  <Fragment key={event.event_id}>
                    <TableRow
                      data-testid={`audit-detail-event-row-${index}`}
                      onClick={() => setSelectedEventId(event.event_id)}
                    >
                      <TableCell data-testid={`audit-detail-event-index-${index}`}>{event.causal_index}</TableCell>
                      <TableCell data-testid={`audit-detail-event-type-${index}`}>{event.event_type}</TableCell>
                      <TableCell data-testid={`audit-detail-event-stage-${index}`}>{event.lifecycle_stage}</TableCell>
                      <TableCell data-testid={`audit-detail-event-severity-${index}`}>{event.severity}</TableCell>
                      <TableCell data-testid={`audit-detail-event-parent-${index}`}>{event.parent_event_id || "-"}</TableCell>
                      <TableCell data-testid={`audit-detail-event-relation-${index}`}>{event.relation_status}</TableCell>
                      <TableCell data-testid={`audit-detail-event-payload-toggle-${index}`}>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(clickEvent) => {
                            clickEvent.stopPropagation();
                            setExpandedEventRows((prev) => ({ ...prev, [event.event_id]: !prev[event.event_id] }));
                          }}
                          data-testid={`audit-detail-event-expand-button-${index}`}
                        >
                          {expandedEventRows[event.event_id] ? "Collapse" : "Expand JSON"}
                        </Button>
                      </TableCell>
                    </TableRow>
                    {expandedEventRows[event.event_id] && (
                      <TableRow data-testid={`audit-detail-event-json-row-${index}`}>
                        <TableCell colSpan={7}>
                          <pre className="max-h-64 overflow-auto rounded bg-slate-950 p-3 text-xs text-emerald-200" data-testid={`audit-detail-event-json-${index}`}>
                            {JSON.stringify(event.payload || {}, null, 2)}
                          </pre>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {!!failureExplanation && (
          <div className="mt-4 rounded-lg border border-amber-800/40 bg-amber-950/20 p-3" data-testid="audit-failure-explanation-panel">
            <p className="text-sm text-amber-100" data-testid="audit-failure-root-cause">root_cause: {failureExplanation.root_cause || "unknown"}</p>
            <p className="text-sm text-amber-100" data-testid="audit-failure-broken-step">broken_step: {failureExplanation.broken_step || "-"}</p>
            <p className="text-sm text-amber-100" data-testid="audit-failure-upstream">upstream: {failureExplanation.upstream_event || "-"}</p>
            <p className="text-sm text-amber-100" data-testid="audit-failure-downstream">downstream: {(failureExplanation.downstream_impact || []).length}</p>
            <p className="text-sm text-amber-100" data-testid="audit-failure-confidence">confidence: {failureExplanation.confidence || "low"}</p>
            <p className="text-sm text-amber-100" data-testid="audit-failure-insufficient-data">insufficient_data: {String(Boolean(failureExplanation.insufficient_data))}</p>
          </div>
        )}

        {!!rootCauseBreakdown && (
          <div className="mt-4 rounded-lg border border-rose-800/40 bg-rose-950/20 p-3" data-testid="audit-root-cause-breakdown-panel">
            <p className="text-sm text-rose-100" data-testid="audit-root-cause-breakdown-type">failure_type: {rootCauseBreakdown.failure_type || "-"}</p>
            <p className="text-sm text-rose-100" data-testid="audit-root-cause-breakdown-pattern">pattern_tag: {rootCauseBreakdown.pattern_tag || "-"}</p>
            <p className="text-sm text-rose-100" data-testid="audit-root-cause-breakdown-cluster">cluster_id: {rootCauseBreakdown.cluster_id || "-"}</p>
            <p className="text-sm text-rose-100" data-testid="audit-root-cause-breakdown-reasons">reason_codes: {(rootCauseBreakdown.reason_codes || []).join(", ") || "none"}</p>
            <p className="text-sm text-rose-100" data-testid="audit-root-cause-breakdown-blockers">critical_blockers: {(rootCauseBreakdown.critical_blockers || []).join(", ") || "none"}</p>
            <p className="text-sm text-rose-100" data-testid="audit-root-cause-breakdown-anomaly">anomaly_detected: {String(Boolean(rootCauseBreakdown.anomaly_detected))}</p>
            <p className="text-sm text-rose-100" data-testid="audit-root-cause-breakdown-anomaly-reasons">anomaly_reasons: {(rootCauseBreakdown.anomaly_reasons || []).join(", ") || "none"}</p>
          </div>
        )}

        {!!replayResult && (
          <div className="mt-4 rounded-lg border border-cyan-800/40 bg-cyan-950/20 p-3" data-testid="audit-replay-result-panel">
            <p className="text-sm text-cyan-100" data-testid="audit-replay-result-status">result: {replayResult.result}</p>
            <p className="text-sm text-cyan-100" data-testid="audit-replay-result-break-step">break_step: {replayResult.break_step?.event_type || "none"}</p>
            <p className="text-sm text-cyan-100" data-testid="audit-replay-result-side-effects">side_effects_blocked: {String(replayResult.side_effects_blocked)}</p>
          </div>
        )}

        {!!crossEnvComparison?.environments && (
          <div className="mt-4 rounded-lg border border-indigo-800/40 bg-indigo-950/20 p-3" data-testid="audit-cross-env-comparison-panel">
            <p className="text-sm font-semibold text-indigo-100" data-testid="audit-cross-env-comparison-title">Cross-environment comparison</p>
            <div className="mt-2 grid gap-2 md:grid-cols-2" data-testid="audit-cross-env-comparison-grid">
              {Object.entries(crossEnvComparison.environments).map(([environment, info]) => (
                <div key={environment} className="rounded border border-indigo-700/40 p-2" data-testid={`audit-cross-env-item-${environment}`}>
                  <p className="text-xs text-indigo-200">{environment}</p>
                  <p className="text-xs text-indigo-100">event_count: {info?.event_count || 0}</p>
                  <p className="text-xs text-indigo-100">broken_chain: {String(Boolean(info?.broken_chain))}</p>
                  <p className="text-xs text-indigo-100">trace_incomplete: {String(Boolean(info?.trace_incomplete))}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {!!integrityResult && (
          <div className="mt-4 rounded-lg border border-emerald-800/40 bg-emerald-950/20 p-3" data-testid="audit-integrity-panel">
            <p className="text-sm text-emerald-100" data-testid="audit-integrity-status">tampered: {String(Boolean(integrityResult.tampered))}</p>
            <p className="text-sm text-emerald-100" data-testid="audit-integrity-events-checked">events_checked: {integrityResult.events_checked || 0}</p>
            <p className="text-sm text-emerald-100" data-testid="audit-integrity-mismatch-count">mismatch_count: {integrityResult.mismatch_count || 0}</p>
          </div>
        )}

        <div className="mt-4 rounded-lg border border-slate-800 bg-slate-900/40 p-3" data-testid="audit-incident-list-panel">
          <p className="text-sm font-semibold text-slate-200" data-testid="audit-incident-list-title">Linked incidents</p>
          {incidents.length === 0 ? (
            <p className="mt-2 text-sm text-slate-400" data-testid="audit-incident-list-empty">incident yok</p>
          ) : (
            <div className="mt-2 space-y-2" data-testid="audit-incident-list-items">
              {incidents.map((incident, idx) => (
                <div key={incident.incident_id} className="flex flex-wrap items-center justify-between gap-3 rounded border border-slate-700 px-3 py-2" data-testid={`audit-incident-item-${idx}`}>
                  <div className="space-y-1">
                    <p className="text-sm text-slate-200" data-testid={`audit-incident-item-id-${idx}`}>{incident.incident_id}</p>
                    <p className="text-xs text-slate-400" data-testid={`audit-incident-item-meta-${idx}`}>
                      {incident.severity} • {incident.status} • auto_created={String(Boolean(incident.auto_created))}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => exportIncidentBundle(incident.incident_id)}
                      data-testid={`audit-incident-export-button-${idx}`}
                    >
                      Export Bundle
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openIncidentLifecycle(incident.correlation_id || incident.linked_correlation_id || incident.details?.correlation_id)}
                      data-testid={`audit-incident-open-lifecycle-button-${idx}`}
                    >
                      Open Lifecycle
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={incident.status === "closed"}
                      onClick={() => closeIncident(incident.incident_id)}
                      data-testid={`audit-incident-close-button-${idx}`}
                    >
                      Close
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-4 rounded-lg border border-cyan-800/40 bg-cyan-950/20 p-3" data-testid="audit-linked-incidents-error-report-section">
            <div className="flex flex-wrap items-center justify-between gap-2" data-testid="audit-linked-incidents-error-report-header">
              <p className="text-sm font-semibold text-cyan-100" data-testid="audit-linked-incidents-error-report-title">
                Global Sistem Hata Raporu (tüm alanlar)
              </p>
              <p className="text-xs text-cyan-200" data-testid="audit-linked-incidents-error-report-meta">
                returned={linkedErrorMeta.returnedCount} · scanned={linkedErrorMeta.totalScanned} · refreshed={linkedErrorMeta.refreshedAt || "-"}
              </p>
            </div>
            <p className="mt-2 text-xs text-cyan-100/90" data-testid="audit-linked-incidents-error-report-description">
              Admin + user + system kaynaklı hata kayıtları tek raporda listelenir.
            </p>

            <div className="mt-3 grid gap-2 md:grid-cols-4" data-testid="audit-global-error-overview-grid">
              {globalErrorOverviewCards.map((card) => (
                <div key={card.key} className="rounded border border-cyan-800/40 bg-slate-950/50 p-3" data-testid={`audit-global-error-overview-card-${card.key}`}>
                  <p className="text-[11px] uppercase tracking-[0.2em] text-cyan-200" data-testid={`audit-global-error-overview-card-label-${card.key}`}>{card.label}</p>
                  <p className="mt-2 text-sm font-semibold text-slate-100" data-testid={`audit-global-error-overview-card-value-${card.key}`}>{String(card.value)}</p>
                </div>
              ))}
            </div>

            <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-4" data-testid="audit-global-error-breakdown-grid">
              {globalErrorBreakdowns.map((group) => (
                <div key={group.key} className="rounded border border-slate-700/70 bg-slate-950/50 p-3" data-testid={`audit-global-error-breakdown-${group.key}`}>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-300" data-testid={`audit-global-error-breakdown-title-${group.key}`}>{group.title}</p>
                  <div className="mt-2 space-y-2" data-testid={`audit-global-error-breakdown-list-${group.key}`}>
                    {group.items.length === 0 ? (
                      <p className="text-xs text-slate-500" data-testid={`audit-global-error-breakdown-empty-${group.key}`}>veri yok</p>
                    ) : (
                      group.items.map((item, index) => (
                        <div key={`${group.key}-${item.key}-${index}`} className="flex items-center justify-between gap-2 rounded border border-slate-800 px-2 py-1" data-testid={`audit-global-error-breakdown-item-${group.key}-${index}`}>
                          <span className="truncate text-xs text-slate-200" title={item.key}>{formatBreakdownLabel(item.key)}</span>
                          <span className="rounded bg-slate-800 px-2 py-0.5 text-[11px] text-cyan-200" data-testid={`audit-global-error-breakdown-count-${group.key}-${index}`}>{item.count}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="audit-linked-incidents-error-report-window-buttons">
              {[1, 7, 30].map((days) => (
                <Button
                  key={days}
                  size="sm"
                  variant={linkedErrorWindowDays === days ? "default" : "outline"}
                  onClick={() => {
                    setLinkedErrorWindowDays(days);
                    setLinkedErrorStartTime("");
                    setLinkedErrorEndTime("");
                    fetchLinkedErrorReport({ windowDays: days, startTime: "", endTime: "", search: linkedErrorSearch });
                  }}
                  data-testid={`audit-linked-incidents-error-report-window-${days}d-button`}
                >
                  {days} gün
                </Button>
              ))}
            </div>

            <div className="mt-3 grid gap-2 md:grid-cols-4" data-testid="audit-linked-incidents-error-report-range-inputs-grid">
              <Input
                type="datetime-local"
                value={linkedErrorStartTime}
                onChange={(event) => setLinkedErrorStartTime(event.target.value)}
                data-testid="audit-linked-incidents-error-report-start-time-input"
              />
              <Input
                type="datetime-local"
                value={linkedErrorEndTime}
                onChange={(event) => setLinkedErrorEndTime(event.target.value)}
                data-testid="audit-linked-incidents-error-report-end-time-input"
              />
              <Input
                value={linkedErrorSearch}
                onChange={(event) => setLinkedErrorSearch(event.target.value)}
                placeholder="endpoint / hata metni ara"
                data-testid="audit-linked-incidents-error-report-search-input"
              />
              <div className="flex flex-wrap items-center gap-2" data-testid="audit-linked-incidents-error-report-actions-group">
                <Button
                  size="sm"
                  onClick={() => fetchLinkedErrorReport({
                    windowDays: linkedErrorWindowDays,
                    startTime: linkedErrorStartTime,
                    endTime: linkedErrorEndTime,
                    search: linkedErrorSearch,
                  })}
                  data-testid="audit-linked-incidents-error-report-apply-button"
                >
                  Raporu Getir
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadLinkedErrorReport("json")}
                  data-testid="audit-linked-incidents-error-report-export-json-button"
                >
                  JSON İndir
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadLinkedErrorReport("xlsx")}
                  data-testid="audit-linked-incidents-error-report-export-xlsx-button"
                >
                  Excel İndir
                </Button>
              </div>
            </div>

            <div className="mt-3 overflow-x-auto" data-testid="audit-linked-incidents-error-report-table-wrap">
              <Table data-testid="audit-linked-incidents-error-report-table">
                <TableHeader>
                  <TableRow>
                    <TableHead data-testid="audit-linked-incidents-error-report-header-trace-id">Trace ID</TableHead>
                    <TableHead data-testid="audit-linked-incidents-error-report-header-time-utc">Zaman (UTC)</TableHead>
                    <TableHead data-testid="audit-linked-incidents-error-report-header-actor-role">Rol</TableHead>
                    <TableHead data-testid="audit-linked-incidents-error-report-header-action">Action</TableHead>
                    <TableHead data-testid="audit-linked-incidents-error-report-header-error-class">Error Class</TableHead>
                    <TableHead data-testid="audit-linked-incidents-error-report-header-endpoint">Endpoint</TableHead>
                    <TableHead data-testid="audit-linked-incidents-error-report-header-http-status">HTTP Status</TableHead>
                    <TableHead data-testid="audit-linked-incidents-error-report-header-message">Hata Mesajı</TableHead>
                    <TableHead data-testid="audit-linked-incidents-error-report-header-retryable">Retryable</TableHead>
                    <TableHead data-testid="audit-linked-incidents-error-report-header-user-id">Kullanıcı ID</TableHead>
                    <TableHead data-testid="audit-linked-incidents-error-report-header-service-source">Service Source</TableHead>
                    <TableHead data-testid="audit-linked-incidents-error-report-header-severity-level">Severity/Level</TableHead>
                    <TableHead data-testid="audit-linked-incidents-error-report-header-correlation-id">Correlation ID</TableHead>
                    <TableHead data-testid="audit-linked-incidents-error-report-header-event-type">Event Type</TableHead>
                    <TableHead data-testid="audit-linked-incidents-error-report-header-rca-tag">RCA Etiketi</TableHead>
                    <TableHead data-testid="audit-linked-incidents-error-report-header-request-id">Request ID</TableHead>
                    <TableHead data-testid="audit-linked-incidents-error-report-header-session-id">Session ID</TableHead>
                    <TableHead data-testid="audit-linked-incidents-error-report-header-client-context">Client Context</TableHead>
                    <TableHead data-testid="audit-linked-incidents-error-report-header-security-mask">Security Mask</TableHead>
                    <TableHead data-testid="audit-linked-incidents-error-report-header-context">Context</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {linkedErrorReportLoading && linkedErrorRows.length === 0 ? (
                    <TableRow data-testid="audit-linked-incidents-error-report-loading-row">
                      <TableCell colSpan={20} className="text-center text-slate-300" data-testid="audit-linked-incidents-error-report-loading-cell">
                        hata raporu yükleniyor...
                      </TableCell>
                    </TableRow>
                  ) : linkedErrorRows.length === 0 ? (
                    <TableRow data-testid="audit-linked-incidents-error-report-empty-row">
                      <TableCell colSpan={20} className="text-center text-slate-400" data-testid="audit-linked-incidents-error-report-empty-cell">
                        seçili aralıkta hata kaydı yok
                      </TableCell>
                    </TableRow>
                  ) : (
                    linkedErrorRows.map((row, index) => (
                      <TableRow key={`${row.trace_id || "trace"}-${index}`} data-testid={`audit-linked-incidents-error-report-row-${index}`}>
                        <TableCell className="font-mono text-xs" data-testid={`audit-linked-incidents-error-report-trace-id-${index}`}>{row.trace_id || "-"}</TableCell>
                        <TableCell className="font-mono text-xs" data-testid={`audit-linked-incidents-error-report-time-utc-${index}`}>{formatUtcDisplay(row.time_utc)}</TableCell>
                        <TableCell data-testid={`audit-linked-incidents-error-report-actor-role-${index}`}>{row.actor_role || "system"}</TableCell>
                        <TableCell className="max-w-[220px] truncate" title={row.action || ""} data-testid={`audit-linked-incidents-error-report-action-${index}`}>{row.action || "-"}</TableCell>
                        <TableCell data-testid={`audit-linked-incidents-error-report-error-class-${index}`}>{row.error_class || "-"}</TableCell>
                        <TableCell className="font-mono text-xs" data-testid={`audit-linked-incidents-error-report-endpoint-${index}`}>{row.endpoint || "-"}</TableCell>
                        <TableCell data-testid={`audit-linked-incidents-error-report-http-status-${index}`}>{row.http_status ?? "-"}</TableCell>
                        <TableCell className="max-w-[380px] truncate" title={row.error_message || ""} data-testid={`audit-linked-incidents-error-report-message-${index}`}>
                          {row.error_message || "-"}
                        </TableCell>
                        <TableCell data-testid={`audit-linked-incidents-error-report-retryable-${index}`}>{String(Boolean(row.retryable))}</TableCell>
                        <TableCell className="font-mono text-xs" data-testid={`audit-linked-incidents-error-report-user-id-${index}`}>{row.user_id || "-"}</TableCell>
                        <TableCell data-testid={`audit-linked-incidents-error-report-service-source-${index}`}>{row.service_source || "System"}</TableCell>
                        <TableCell data-testid={`audit-linked-incidents-error-report-severity-level-${index}`}>
                          <span className={`rounded px-2 py-1 text-xs font-semibold ${severityLevelBadgeClass[String(row.severity_level || "INFO").toUpperCase()] || severityLevelBadgeClass.INFO}`}>
                            {String(row.severity_level || "INFO").toUpperCase()}
                          </span>
                        </TableCell>
                        <TableCell className="font-mono text-xs" data-testid={`audit-linked-incidents-error-report-correlation-id-${index}`}>{row.correlation_id || "-"}</TableCell>
                        <TableCell data-testid={`audit-linked-incidents-error-report-event-type-${index}`}>{row.event_type || "unknown_event"}</TableCell>
                        <TableCell data-testid={`audit-linked-incidents-error-report-rca-tag-${index}`}>
                          <span className={`rounded px-2 py-1 text-xs font-semibold ${rcaTagBadgeClass[String(row.rca_tag || "-").toUpperCase()] || rcaTagBadgeClass.UNKNOWN}`}>
                            {String(row.rca_tag || "-").toUpperCase()}
                          </span>
                        </TableCell>
                        <TableCell className="font-mono text-xs" data-testid={`audit-linked-incidents-error-report-request-id-${index}`}>{row.request_id || "-"}</TableCell>
                        <TableCell className="font-mono text-xs" data-testid={`audit-linked-incidents-error-report-session-id-${index}`}>{row.session_id || "-"}</TableCell>
                        <TableCell className="max-w-[260px] truncate" title={formatClientContextDisplay(row.client_context)} data-testid={`audit-linked-incidents-error-report-client-context-${index}`}>
                          {formatClientContextDisplay(row.client_context)}
                        </TableCell>
                        <TableCell data-testid={`audit-linked-incidents-error-report-security-mask-${index}`}>{String(Boolean(row.security_mask))}</TableCell>
                        <TableCell className="max-w-[340px]" data-testid={`audit-linked-incidents-error-report-context-${index}`}>
                          <details data-testid={`audit-linked-incidents-error-report-context-details-${index}`}>
                            <summary className="cursor-pointer text-xs text-cyan-200">JSON aç</summary>
                            <pre className="mt-2 max-h-56 overflow-auto rounded bg-slate-950 p-2 text-[11px] text-emerald-200" data-testid={`audit-linked-incidents-error-report-context-json-${index}`}>
                              {JSON.stringify(row.context_payload || {}, null, 2)}
                            </pre>
                          </details>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          <div className="mt-4 border-t border-slate-700/60 pt-3" data-testid="audit-error-list-section">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-semibold text-rose-200" data-testid="audit-error-list-title">Errors (en altta)</p>
              <div className="flex items-center gap-2" data-testid="audit-error-window-filter-group">
                {[1, 7, 30].map((days) => (
                  <Button
                    key={days}
                    size="sm"
                    variant={errorWindowDays === days ? "default" : "outline"}
                    onClick={() => {
                      setErrorWindowDays(days);
                      setErrorLogPage(1);
                    }}
                    data-testid={`audit-error-window-filter-${days}d-button`}
                  >
                    {days} gün
                  </Button>
                ))}
              </div>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-300" data-testid="audit-error-list-meta-row">
              <span data-testid="audit-error-list-meta-total">son_250_hata_toplamı: {errorLogMeta.total}</span>
              <span data-testid="audit-error-list-meta-page">sayfa: {errorLogPage}/{errorLogMeta.pageCount || 1}</span>
              <span data-testid="audit-error-list-meta-range">aralık: {errorLogPageLabel}</span>
              <span data-testid="audit-error-list-meta-refreshed">güncellendi: {errorLogMeta.refreshedAt || "-"}</span>
            </div>

            {errorLogLoading && errorLogItems.length === 0 ? (
              <p className="mt-2 text-sm text-slate-400" data-testid="audit-error-list-loading">hata logları yükleniyor...</p>
            ) : errorLogItems.length === 0 ? (
              <p className="mt-2 text-sm text-slate-400" data-testid="audit-error-list-empty">Seçili pencerede hata yok.</p>
            ) : (
              <div className="mt-2 space-y-2" data-testid="audit-error-list-items">
                {errorLogItems.map((row, idx) => (
                  <div key={`${row.id}-err-${idx}`} className="rounded border border-rose-700/50 bg-rose-950/20 px-3 py-2" data-testid={`audit-error-item-${idx}`}>
                    <p className="text-sm text-rose-100" data-testid={`audit-error-item-id-${idx}`}>{row.id}</p>
                    <p className="text-xs text-rose-200/90" data-testid={`audit-error-item-meta-${idx}`}>
                      {String(row.severity || "-").toUpperCase()} • {row.error_class || "trade_blocker"} • {row.created_at || "tarih_yok"}
                    </p>
                    <p className="text-xs text-rose-100/90" data-testid={`audit-error-item-rca-${idx}`}>
                      RCA: {String(row.rca_tag || "-").toUpperCase()}
                    </p>
                    <p className="mt-1 text-xs text-rose-100" data-testid={`audit-error-item-action-${idx}`}>action: {row.action || "-"}</p>
                    <p className="text-xs text-rose-100/90" data-testid={`audit-error-item-message-${idx}`}>
                      {row.error_message || "hata mesajı yok"}
                    </p>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-3 flex items-center gap-2" data-testid="audit-error-list-pagination-controls">
              <Button
                size="sm"
                variant="outline"
                disabled={!errorLogMeta.hasPrev || errorLogLoading}
                onClick={() => setErrorLogPage((prev) => Math.max(prev - 1, 1))}
                data-testid="audit-error-list-pagination-prev-button"
              >
                Önceki 50
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={!errorLogMeta.hasNext || errorLogLoading}
                onClick={() => setErrorLogPage((prev) => prev + 1)}
                data-testid="audit-error-list-pagination-next-button"
              >
                Sonraki 50
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
